import Badge from '@pelckmans/elementis/components/badge';
import Card from '@pelckmans/elementis/components/card';
import Pill from '@pelckmans/elementis/components/pill';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExerciseCategory, getExerciseCategories } from '../../../../api/exercise-category';
import { Module } from '../../../../api/module';
import ModuleStatus from '../../../../components/ModuleStatus';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import IdentityContext from '../../../../contexts/IdentityContext';
import useAnalytics from '../../../../hooks/useAnalytics';
import { useRoutes } from '../../../../hooks/useRoutes';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { hasAccess } from '../../../my-methods/utils';
import ModuleCardTags from './ModuleCardTags';

function ExerciseCategories({ categories }: { categories: ExerciseCategory[] }) {
  return (
    <>
      {categories.map(category => {
        const badgeProps = category.usage > 0 ? { badge: <Badge>{category.usage}</Badge> } : {};

        return (
          <Pill role="listitem" key={category.id} {...badgeProps}>
            {category.name}
          </Pill>
        );
      })}
    </>
  );
}

export default function ModuleCard({ module, isVirtual = false }: { module: Module; isVirtual?: boolean }) {
  const [exerciseCategories, setExerciseCategories] = useState<ExerciseCategory[]>([]);

  const { identity } = useContext(IdentityContext);
  const { buildRelativePath } = useRoutes();
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();

  const { bookType, externalContentUrl, id, isExerciseModule, niceName, publishingState, slug, styling } = module;

  const captureModuleAccessed = async () => {
    await analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.MODULE_ACCESSED,
      objectId: id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SUBMODULE_SELECTION,
    });
  };

  const captureExerciseModuleAccessed = async () => {
    await analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.VIRTUAL_EXERCISE_MODULE_ACCESSED,
      objectId: id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SUBMODULE_SELECTION,
    });
  };

  const fetchExerciseCategories = useCallback(
    async (moduleId: string) => {
      try {
        const { data } = await getExerciseCategories({
          moduleId,
          includeSubModules: !isExerciseModule,
        });

        const general = data.find(({ id }) => id === 'general');
        const rest = data.filter(({ id }) => id !== 'general');

        const flattenedThemes = [...(general?.categories?.map(category => ({ ...category, usage: 0 })) || []), ...rest];
        setExerciseCategories(flattenedThemes);
      } catch (e) {
        handleError(e);
      }
    },
    [isExerciseModule, handleError],
  );

  useEffect(() => {
    if (isExerciseModule || isVirtual) fetchExerciseCategories(id);
  }, [isExerciseModule, isVirtual, fetchExerciseCategories, id]);

  if (isVirtual) {
    return (
      <Card
        as="link"
        image="placeholder"
        imagePosition="left"
        titleTag="h4"
        titleText={t('PAGES.SUBMODULE_SELECTOR.EXERCISE_MODULE.TITLE')}
        subText={t('PAGES.SUBMODULE_SELECTOR.EXERCISE_MODULE.SUBTEXT')}
        href={buildRelativePath('MATERIAL.ASSIGNMENTS', {
          selectedModuleSlug: module.slug,
        })}
        onClick={captureExerciseModuleAccessed}
        tags={{
          role: 'list',
          children: <ExerciseCategories categories={exerciseCategories} />,
          'aria-label': t('PAGES.SUBMODULE_SELECTOR.EXERCISE_MODULE.TAGS.A11Y_LABEL'),
        }}
      />
    );
  }

  const externalProps = Boolean(externalContentUrl)
    ? {
        external: true as const,
        externalAriaLabel: t('PAGES.SUBMODULE_SELECTOR.EXTERNAL.A11Y_LABEL'),
      }
    : {};

  return (
    <Card
      key={id}
      as="link"
      image={styling?.cover || 'placeholder'}
      imagePosition="left"
      titleTag="h4"
      titleText={niceName}
      subText={bookType}
      href={externalContentUrl || buildRelativePath('MATERIAL.TOC', { selectedModuleSlug: slug, nodeId: '' })}
      tags={{
        role: 'list',
        children: isExerciseModule ? (
          <ExerciseCategories categories={exerciseCategories} />
        ) : (
          <ModuleCardTags module={module} />
        ),
        'aria-label': t('PAGES.SUBMODULE_SELECTOR.TAGS.A11Y_LABEL', {
          moduleName: niceName,
        }),
      }}
      disabled={!hasAccess(identity, publishingState, id)}
      status={{
        children: (
          <ModuleStatus
            access={hasAccess(identity, module.publishingState, module.id)}
            module={module}
            showUnlicensedStatus
          />
        ),
        'aria-label': undefined,
        role: undefined,
      }}
      target={externalContentUrl ? '_blank' : undefined}
      onClick={captureModuleAccessed}
      {...externalProps}
    />
  );
}
